/**
 * ops-log — fire-and-forget client utility for non-sensitive operational events.
 *
 * Calls the cell API Gateway directly at /edge-lambda-service/ops-log.
 * The Lambda emits a CloudWatch EMF metric visible to the ops account dashboards/alarms.
 *
 * Security contract — DO NOT include in message:
 *   - user IDs, email addresses, or names
 *   - file paths, case names, or document titles
 *   - org/tenant display names
 *   - any content from uploaded documents
 *
 * event_type is drawn from {@link OPS_EVENT_TYPES} — a closed set, so a typo is
 * a compile error rather than a silent 422 in production.
 *
 * Receiver contract (`edge_lambda_service/handlers/ops_log.py`). A violation is
 * invisible from here — the response is discarded — so {@link reportOpsEvent}
 * enforces each bound locally instead of trusting callers:
 *   - `event_type` must match /^[A-Z][A-Z0-9_]*$/ and be ≤ 64 chars (else 422).
 *   - `message` must be 1–512 chars (else 422).
 *   - The route requires an authenticated Cognito user **with a tenant claim**
 *     (`require_tenant` → 401). `tenant_id` comes from the JWT; callers cannot
 *     supply it, so per-tenant attribution is automatic.
 *
 * Calling this is safe inside a render or error path, but not entirely free of
 * side effects: delivery goes through `gatewayFetch`, whose auth recovery treats
 * a 401/403 (and an opaque network failure) as a possibly-stale token and may
 * force one `/api/init` refresh. Never call it from a path that fires *because*
 * authentication is broken — see `reportOpsEvent`.
 */

import { gatewayFetch, getGatewaySession } from '@/lib/gateway-fetch';
import { TRANSIENT_PDF_WORKER_MARKERS } from '@/lib/pdf-worker-errors';

export type OpsLogSeverity = 'ERROR' | 'WARN' | 'INFO';

/**
 * Every event code the UI may emit — the single source of truth.
 *
 * Deliberately a closed set rather than free-form strings. The receiver rejects a
 * malformed code with a 422 that this module cannot observe, so an invalid code
 * would fail silently and forever. Keeping them here means the compiler rejects
 * a typo, and the unit test enumerates the real list instead of a hand-copied
 * echo of it (the kind of duplication that already bit us once — see
 * `TRANSIENT_PDF_WORKER_MARKERS`).
 */
export const OPS_EVENT_TYPES = {
  /** Root-layout crash caught by `app/global-error.tsx`. */
  APP_CRASH: 'APP_CRASH',
  /** PDF/document viewer boundary crash, excluding pdf.js worker teardown. */
  DOCUMENT_VIEWER_CRASH: 'DOCUMENT_VIEWER_CRASH',
  /** MediaDisplay boundary crash (image / capture still / player). */
  MEDIA_VIEWER_CRASH: 'MEDIA_VIEWER_CRASH',
  // No MARKDOWN_EDITOR_CRASH: `components/ui/markdownEditor.tsx` — and the
  // ErrorBoundary it carried — was deleted on main in a277b220 (buttons migrated
  // to theme-components). The editor now comes from
  // `@tranquility-ai/theme-components/markdown-editor` at five call sites, none of
  // which wraps it in a boundary, so there is nothing here to hook. A code with no
  // emitter would contradict this set's promise of being what the UI may emit.
  /** permission-service unreachable or non-2xx; admin state falls back to false. */
  PERMISSION_SERVICE_UNAVAILABLE: 'PERMISSION_SERVICE_UNAVAILABLE',
  /** LaunchDarkly identify failed; flags fall back to defaults. */
  FEATURE_FLAGS_IDENTIFY_FAILED: 'FEATURE_FLAGS_IDENTIFY_FAILED',
} as const;

export type OpsEventType = (typeof OPS_EVENT_TYPES)[keyof typeof OPS_EVENT_TYPES];

export interface OpsLogPayload {
  event_type: OpsEventType;
  severity: OpsLogSeverity;
  /** Sanitized human-readable description. No PII — see module docstring. */
  message: string;
}

/**
 * Receiver-side validation, mirrored here and **enforced** in
 * {@link reportOpsEvent} — not merely asserted in a test. A 422 is invisible to
 * this module (the response is discarded), so an over-long message would vanish
 * with no symptom at either end. See
 * `edge_lambda_service/handlers/ops_log.py::OpsLogBody`.
 */
export const OPS_EVENT_TYPE_PATTERN = /^[A-Z][A-Z0-9_]*$/;
export const OPS_EVENT_TYPE_MAX_LENGTH = 64;
export const OPS_MESSAGE_MAX_LENGTH = 512;

/**
 * Report a non-sensitive operational event. Never throws — failures are
 * swallowed so a logging call never breaks the user-facing flow.
 *
 * Dropped without a request when the session carries no tenant. The receiver
 * gates on `require_tenant`, which answers **401** — and a 401 from a cell route
 * is indistinguishable from a stale token to `gatewayFetch`'s auth recovery,
 * which forces an `/api/init` refresh and, when the Cognito session is genuinely
 * dead, redirects to sign-out. Reporting an event is never worth logging a user
 * out, so a tenant-less caller is a no-op by design (this is why failures of
 * *authentication itself* cannot be reported here).
 */
export async function reportOpsEvent(payload: OpsLogPayload): Promise<void> {
  try {
    const session = await getGatewaySession();
    if (!session?.tenantId) return;

    const body = conformToReceiver(payload);
    if (body === null) return;

    await gatewayFetch(
      '/edge-lambda-service',
      '/ops-log',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      session,
    );
  } catch {
    // Intentionally swallowed — ops logging must not interrupt the UI.
  }
}

/**
 * Coerce a payload into something the receiver will accept, or `null` to drop it.
 *
 * Truncates an over-long message rather than dropping the event: callers reach
 * this point having already sanitized their text (see {@link describeErrorForOps},
 * which *drops* instead — its input is untrusted), and a first-512-chars record of
 * a real crash beats no record at all. `event_type` is the opposite case: it is
 * the metric dimension, so a mangled one would create a junk series — drop.
 *
 * An empty message is also a 422, and the only realistic source is a caller
 * interpolating an empty constant, so it becomes a marker we can search for.
 */
function conformToReceiver(payload: OpsLogPayload): OpsLogPayload | null {
  const { event_type, severity, message } = payload;
  if (!OPS_EVENT_TYPE_PATTERN.test(event_type)) return null;
  if (event_type.length > OPS_EVENT_TYPE_MAX_LENGTH) return null;

  const trimmed = typeof message === 'string' ? message.trim() : '';
  return {
    event_type,
    severity,
    message: trimmed === '' ? 'unspecified' : trimmed.slice(0, OPS_MESSAGE_MAX_LENGTH),
  };
}

/** Convenience wrapper for ERROR-severity events. */
export const reportOpsError = (event_type: OpsEventType, message: string) =>
  reportOpsEvent({ event_type, severity: 'ERROR', message });

/** Convenience wrapper for WARN-severity events. */
export const reportOpsWarn = (event_type: OpsEventType, message: string) =>
  reportOpsEvent({ event_type, severity: 'WARN', message });

/**
 * Reported keys, so a self-retrying error boundary bills us once, not per cycle.
 *
 * Lives for the page's lifetime and is never cleared: an error boundary that
 * remounts a permanently-broken subtree (`pdfViewer` bumps a `resetKeys` entry
 * from inside `onError`) re-throws immediately, so a per-render report becomes an
 * unbounded stream of identical CloudWatch datapoints. One datapoint per distinct
 * failure is all the dashboard can use — the alarm counts *affected sessions*.
 */
const reportedOnce = new Set<string>();

/**
 * ERROR-severity report, at most once per `dedupeKey` per page load.
 *
 * `dedupeKey` never leaves the browser — it is a Set key only — so unlike
 * `message` it may safely contain identifying data (a presigned URL, a file
 * name), which is usually what makes one failure distinct from the next.
 */
export function reportOpsErrorOnce(
  dedupeKey: string,
  event_type: OpsEventType,
  message: string,
): void {
  const key = `${event_type} ${dedupeKey}`;
  if (reportedOnce.has(key)) return;
  reportedOnce.add(key);
  void reportOpsError(event_type, message);
}

/**
 * Purely technical message fragments that are safe to forward verbatim.
 *
 * Matching is substring + case-insensitive. Every entry must be a fixed
 * library/platform string that cannot carry caller data.
 */
const SAFE_MESSAGE_MARKERS: readonly string[] = [
  'Failed to fetch',
  'NetworkError',
  'Load failed',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'The user aborted a request',
  'signal is aborted',
  'timeout',
  // Spread, not restated. These were hand-copied here and in the viewer's
  // transient-error classifier until the two drifted: `Worker was destroyed` was
  // listed here only, so pdf.js teardown got reported as a crash (Bugbot, !398).
  // A teardown marker added to that module is now automatically safe to forward.
  ...TRANSIENT_PDF_WORKER_MARKERS,
  // Not a teardown race — a genuinely malformed document, worth reporting.
  'Invalid PDF structure',
  'out of memory',
];

/** Error class names are code identifiers; reject anything else outright. */
const ERROR_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/**
 * Describe an unknown thrown value for an ops-log `message`, PII-safely.
 *
 * A raw `error.message` is NOT usable here. pdf.js, `fetch`, and MDX errors
 * routinely interpolate presigned S3 URLs, file names, and document titles into
 * their messages — exactly what this module's security contract forbids. So we
 * emit only:
 *
 *   1. the error's class name (`TypeError`, `ChunkLoadError`, …), and
 *   2. at most one fragment from {@link SAFE_MESSAGE_MARKERS}, when the message
 *      contains a known-technical marker.
 *
 * Anything unrecognized is dropped rather than truncated — a truncated message
 * is still a message, and a prefix is as likely to leak a file name as the tail.
 * Callers that need more context should pass their own constant string (an
 * event code, an HTTP status), never interpolated user or document data.
 */
export function describeErrorForOps(error: unknown): string {
  const rawName = error instanceof Error ? error.name : typeof error;
  const name = ERROR_NAME_PATTERN.test(rawName) ? rawName : 'UnknownError';

  let rawMessage = '';
  if (error instanceof Error && typeof error.message === 'string') {
    rawMessage = error.message;
  }
  const haystack = rawMessage.toLowerCase();
  const marker = SAFE_MESSAGE_MARKERS.find((m) => haystack.includes(m.toLowerCase()));

  return marker ? `${name}: ${marker}` : name;
}
